'use client';

import { BRAND, storeHomeUrl, withBase } from '../lib/brand';
import React, { Suspense, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { isSharePagePath } from '../lib/sharePage';
import { useCaPreview } from '../lib/caPreview';

/** window event: detail is the explorer task to show as the last crumb, or null. */
export const CRUMB_TAIL_EVENT = 'ca-crumb-tail';
/** window event: the Campsite Explorer crumb was clicked while a task crumb shows. */
export const CRUMB_BACK_EVENT = 'ca-crumb-back';

interface BreadcrumbItem {
  title: string;
  href: string;
  icon?: boolean;
  current?: boolean;
}

// Separate component that uses useSearchParams
function BreadcrumbsContent() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  // ca_preview: read like the store's breadcrumb — "Home" is the shop,
  // then Campsite Explorer, then where you are.
  const preview = useCaPreview();
  // ca_preview: the explorer names its current task (e.g. "Trip planner")
  // as the last crumb, so every planner view reads Home > Campsite Explorer > ...
  // like the store, rather than a second trail of its own in the results bar.
  const [tail, setTail] = useState<string | null>(null);
  useEffect(() => {
    const on = (e: Event) => setTail(((e as CustomEvent<string | null>).detail) || null);
    const w = window as unknown as { __caCrumbTail?: string | null };
    if (w.__caCrumbTail) setTail(w.__caCrumbTail);
    window.addEventListener(CRUMB_TAIL_EVENT, on);
    return () => window.removeEventListener(CRUMB_TAIL_EVENT, on);
  }, []);

  // Clean up the pathname to ensure no double slashes or trailing slashes
  // The Shopify app proxy forwards /apps/campsite-explorer/regions/x to the
  // server as /regions/x, while the browser's pathname keeps the prefix. The
  // trail used to be built from whichever the render saw, so the server's
  // "Regions › Grampians" met the client's "Campsite Explorer › Regions ›
  // Grampians" and /regions threw React #425/#422 on every load. Build it
  // from the app path (prefix stripped) and put the prefix back the same way
  // on both sides.
  const base = BRAND.basePath;
  const rawPath = (pathname || '/').replace(/\/+/g, '/');
  const appPath = (base && (rawPath === base || rawPath.startsWith(`${base}/`)) ? rawPath.slice(base.length) : rawPath).replace(/\/$/, '');
  const cleanPathname = `${base}${appPath}`.replace(/\/$/, '') || '/';
  const pathSegments = cleanPathname.split('/').filter(segment => segment && segment !== 'apps');
  
  // Strip Shopify app-proxy params (shop/signature/timestamp/path_prefix/
  // logged_in_customer_id) — they are signed + single-use and must NEVER be
  // baked into internal links, or crawlers follow them and 404 on the expired
  // signature (the cause of tens of thousands of SEMrush "broken link" 404s).
  const PROXY_PARAMS = ['shop', 'signature', 'timestamp', 'path_prefix', 'logged_in_customer_id', 'ca_preview'];
  const cleanParams = new URLSearchParams();
  searchParams?.forEach((v, k) => { if (!PROXY_PARAMS.includes(k)) cleanParams.set(k, v); });
  const cleanQuery = cleanParams.toString();

  // Check if we're on a campsite detail page AND have a REAL search context
  const isOnCampsiteDetail = pathSegments.includes('camp-sites');
  const hasSearchParams = cleanQuery.length > 0;
  const showBackToSearch = isOnCampsiteDetail && hasSearchParams;

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [
      { title: 'Home', href: '/', icon: true }
    ];

    let currentPath = '';

    // Process path segments
    pathSegments.forEach((segment, index) => {
      // Clean the segment to ensure no slashes
      segment = segment.trim();
      
      // Build the path properly without showing it in the title
      if (index === 0) {
        currentPath = `/${segment}`;
      } else {
        currentPath = `${currentPath}/${segment}`;
      }

      if (segment === 'campsite-explorer') {
        if (preview) {
          items.push({
            title: 'Campsite Explorer',
            href: withBase(''),
            current: pathSegments.length === 1 && !tail,
          });
          if (pathSegments.length === 1 && tail) {
            items.push({ title: tail, href: '#', current: true });
          }
        // Only show as breadcrumb if we're deeper than the main page
        } else if (pathSegments.length > 1) {
          items.push({ 
            title: 'Campsite Explorer', 
            href: withBase('')
          });
        }
      } else if (segment === 'regions') {
        if (index < pathSegments.length - 1 || pathSegments.length > 2) {
          items.push({ 
            title: 'Regions', 
            href: withBase('/regions')
          });
        } else {
          items.push({ 
            title: 'Regions', 
            href: withBase('/regions'),
            current: true
          });
        }
      } else if (segment === 'camp-sites') {
        // Skip camp-sites segment - handled by "Back to Search" when applicable
      } else if (segment === 'campervan-hire') {
        items.push({
          title: 'Campervan hire',
          href: withBase('/campervan-hire'),
          current: index === pathSegments.length - 1,
        });
      } else if (index === pathSegments.length - 1 && pathSegments[index - 1] === 'campervan-hire') {
        // A campervan detail page (campervan-hire/<slug>).
        const title = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        items.push({ title, href: cleanPathname, current: true });
      } else if (index === pathSegments.length - 1 && pathSegments[index - 1] === 'regions') {
        // This is a region detail page
        const title = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        items.push({ 
          title, 
          href: cleanPathname,
          current: true 
        });
      } else if (index === pathSegments.length - 1 && pathSegments[index - 1] === 'camp-sites') {
        // This is the campsite name - add it to breadcrumbs
        const title = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        items.push({ 
          title, 
          href: cleanPathname,
          current: true 
        });
      }
    });

    return items;
  };

  const renderHomeLink = () => {
    if (preview) {
      return (
        <a href={storeHomeUrl()} className="breadcrumb-link ca-crumb-home">
          {/* The store breadcrumb's own house mark, at its size. */}
          <svg className="home-icon ca-crumb-house" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
          </svg>
          <span className="home-text">Home</span>
        </a>
      );
    }
    const handleHomeClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (pathname === withBase('')) {
        window.location.reload();
      } else {
        router.push(withBase(''));
      }
    };

    return (
      <a 
        href={withBase("")}
        onClick={handleHomeClick}
        className="breadcrumb-link"
        aria-label="Campsite Explorer Home"
      >
        <svg className="home-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
        </svg>
      </a>
    );
  };

  // Back-to-search URL — preserves only REAL search params (proxy params stripped)
  const backToSearchUrl = withBase(`${cleanQuery ? `?${cleanQuery}` : ''}`);

  const handleBackToSearch = (e: React.MouseEvent) => {
    e.preventDefault();
    // If there's browser history, go back to preserve scroll/state.
    // Otherwise (e.g. opened in a new tab), navigate to the search URL.
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push(backToSearchUrl);
    }
  };

  return (
    <div className="breadcrumbs-wrapper">
      <nav className="modern-breadcrumbs" role="navigation" aria-label="breadcrumbs">
        <div className="breadcrumbs-container">
          <div className="breadcrumbs-content">
            {/* Back to Search - Left Side */}
            {showBackToSearch && (
              <>
                <a 
                  href={backToSearchUrl}
                  onClick={handleBackToSearch}
                  className="back-to-search-link"
                >
                  <svg className="back-arrow-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                  Back to Search Results
                </a>
                <div className="vertical-separator"></div>
              </>
            )}
            
            {/* Regular Breadcrumbs - Also Left Side */}
            <ol className="breadcrumb-list">
              {getBreadcrumbItems().map((item, index, array) => (
                <li key={item.href || index} className="breadcrumb-item">
                  {item.icon ? (
                    renderHomeLink()
                  ) : item.href === '#' ? (
                    <span className="breadcrumb-link current" aria-current="page">{item.title}</span>
                  ) : (
                    <Link 
                      href={item.href}
                      onClick={tail && item.title === 'Campsite Explorer' ? (e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent(CRUMB_BACK_EVENT)); } : undefined}
                      className={`breadcrumb-link ${item.current ? 'current' : ''}`}
                      aria-current={item.current ? 'page' : undefined}
                    >
                      {item.title}
                    </Link>
                  )}
                  {index < array.length - 1 && !item.current && (
                    <svg className="separator-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </nav>

      <style jsx>{`
        .breadcrumbs-wrapper {
          background: #f8f9fa;
          border-bottom: 1px solid #eee;
        }

        .modern-breadcrumbs {
          padding: 12px 0;
          font-size: 14px;
          color: #666;
        }

        .breadcrumbs-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .breadcrumbs-content {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0;
        }

        :global(.back-to-search-link) {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #1558b0;
          text-decoration: none;
          transition: color 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
          margin-right: 16px;
        }

        :global(.back-to-search-link:hover) {
          color: #1e3a8a;
        }

        .vertical-separator {
          width: 1px;
          height: 20px;
          background-color: #ddd;
          margin-right: 16px;
          flex-shrink: 0;
        }

        .breadcrumb-list {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          margin: 0;
          padding: 0;
          list-style: none;
          gap: 4px;
        }

        .breadcrumb-list a::before {
          content: none !important;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
        }

        :global(.breadcrumb-link) {
          display: flex;
          align-items: center;
          color: #666;
          text-decoration: none;
          transition: color 0.2s ease;
          white-space: nowrap;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        :global(.breadcrumb-link:hover) {
          color: #DC2626;
        }

        :global(.breadcrumb-link.current) {
          color: #111;
          font-weight: 500;
          max-width: none;
        }

        :global(.home-icon) {
          stroke: currentColor;
          width: 20px;
          height: 20px;
        }

        :global(.back-arrow-icon) {
          stroke: currentColor;
          width: 16px;
          height: 16px;
        }

        :global(.separator-icon) {
          margin: 0 6px;
          stroke: #999;
          flex-shrink: 0;
        }

        .visually-hidden {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          border: 0;
        }

        @media (max-width: 768px) {
          .modern-breadcrumbs {
            padding: 8px 0;
            font-size: 12px;
          }

          .breadcrumbs-container {
            padding: 0 16px;
          }

          .breadcrumbs-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          :global(.back-to-search-link) {
            font-size: 14px;
            margin-right: 0;
            margin-bottom: 8px;
          }

          .vertical-separator {
            display: none;
          }

          /* Keep the trail on a single line so a long product title
             truncates with an ellipsis instead of pushing the row to
             two lines (preserves vertical real estate for images). */
          .breadcrumb-list {
            margin-left: 0;
            flex-wrap: nowrap;
            width: 100%;
            min-width: 0;
          }

          .breadcrumb-item:last-child {
            flex: 1 1 auto;
            min-width: 0;
            overflow: hidden;
          }

          :global(.breadcrumb-link) {
            max-width: 150px;
          }

          /* Override the desktop 'max-width: none' for the current
             item so the long product title gets ellipsis-truncated.
             Backticks here break the styled-jsx template literal. */
          :global(.breadcrumb-link.current) {
            max-width: 100%;
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        @media (prefers-color-scheme: dark) {
          .breadcrumbs-wrapper {
            background: #1a1a1a;
            border-color: #333;
          }

          .vertical-separator {
            background-color: #444;
          }

          :global(.breadcrumb-link) {
            color: #999;
          }

          :global(.breadcrumb-link.current) {
            color: #fff;
          }

          :global(.back-to-search-link) {
            color: #60a5fa;
          }

          :global(.back-to-search-link:hover) {
            color: #a8d4f5;
          }

          :global(.separator-icon) {
            stroke: #666;
          }
        }
      `}</style>
    </div>
  );
}

// Main component with Suspense boundary
const Breadcrumbs = () => {
  // A shared trip has its own slim bar; a lone home icon here only cost 45px.
  const pathname = usePathname();
  if (isSharePagePath(pathname)) return null;
  return (
    <Suspense fallback={
      <div className="breadcrumbs-wrapper" style={{ background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
        {/* Same vertical padding as the real bar at each breakpoint (12px,
            8px at <=768px). The fallback was 12px everywhere, so on a phone
            the bar shrank 45px -> 37px when it hydrated and the page moved up
            (layout-shift trace, 5 Oct 2026). */}
        <style>{'.breadcrumbs-fallback{padding:12px 0}@media (max-width:768px){.breadcrumbs-fallback{padding:8px 0}}'}</style>
        <nav className="modern-breadcrumbs breadcrumbs-fallback">
          <div className="breadcrumbs-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
            <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
          </div>
        </nav>
      </div>
    }>
      <BreadcrumbsContent />
    </Suspense>
  );
};

export default Breadcrumbs;